import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, RotateCcw, Plus } from 'lucide-react';
import { BulkActionsBar } from '@synotis/ui';
import { apiAffaireTeam, apiAffaireMargin } from '../api/client.js';
import Avatar from './Avatar.jsx';
import Modal from './Modal.jsx';
import PeoplePicker from './PeoplePicker.jsx';
import { hourlyToTjm, tjmToHourly } from '../lib/units.js';

/**
 * Sprint 7 — Tab Team d'une Affaire (pattern Float §2).
 *
 * Props :
 *  - affaireId : id de l'affaire (string)
 *  - canEdit   : booléen (ADMIN ou niveau délégué, #108). Si false, lecture seule.
 *  - showRates : #108 — false au niveau `read` (aucun taux, pas de marge).
 *  - showCostRates : #108 — false hors ADMIN : le CJM individuel (et la marge
 *                par membre, qui le révélerait) n'est ni affiché ni envoyé.
 *  - onCountChange : (count: number) => void — pour mettre à jour le compteur
 *                    du tab parent (Float-style "Team1").
 *
 * Affiche :
 *  - Bandeau marge agrégée (totalCost / totalRevenue / totalMargin)
 *  - Liste des membres actifs (Avatar + nom + rôle effectif + cost/bill/margin)
 *  - Bouton "Ajouter" → PeoplePicker (combobox)
 *  - Edit modal : overrides roleOverride / costRateOverride / billRateOverride
 *  - Archive / Restore via icônes
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const iconBtn = {
  background: 'transparent',
  border: 'none',
  padding: 4,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/** Taux horaire stocké → CHF/jour (×8). */
function formatRate(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 8).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} CHF/j`;
}

function formatMoney(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function formatHours(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`;
}

export default function AffaireTeamTab({
  affaireId,
  canEdit,
  onCountChange,
  showRates = true,
  showCostRates = true,
}) {
  const [members, setMembers] = useState([]);
  const [archivedMembers, setArchivedMembers] = useState([]);
  const [margin, setMargin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    roleOverride: '',
    costRateOverride: '',
    billRateOverride: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);
  // synotis-support#9 — multisélection des membres (actifs et archivés
  // affichés) pour un retrait groupé.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!affaireId) return;
    setLoading(true);
    setError(null);
    try {
      const [active, archived, marg] = await Promise.all([
        apiAffaireTeam.list(affaireId, 'active'),
        apiAffaireTeam.list(affaireId, 'archived'),
        showRates ? apiAffaireMargin.get(affaireId).catch(() => null) : Promise.resolve(null),
      ]);
      setMembers(active.items);
      setArchivedMembers(archived.items);
      setSelectedIds(new Set());
      setMargin(marg);
      onCountChange?.(active.items.length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [affaireId, onCountChange, showRates]);

  useEffect(() => {
    reload();
  }, [reload]);

  const fetchPickerOptions = useCallback(
    async (q) => {
      try {
        const { items } = await apiAffaireTeam.available(affaireId, q, 20);
        return items;
      } catch {
        return [];
      }
    },
    [affaireId],
  );

  const onAdd = async (option) => {
    if (!option) return;
    setError(null);
    try {
      await apiAffaireTeam.create(affaireId, { personId: option.id });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const openEdit = (member) => {
    setEditing(member);
    setEditForm({
      roleOverride: member.roleOverride ?? '',
      // Overrides affichés/saisis en journalier (CHF/jour) : ×8 depuis horaire.
      costRateOverride: hourlyToTjm(member.costRateOverride ?? ''),
      billRateOverride: hourlyToTjm(member.billRateOverride ?? ''),
    });
    setEditError(null);
  };

  const onSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSubmitting(true);
    setEditError(null);
    try {
      const role = editForm.roleOverride.trim();
      // Saisie journalière (CHF/jour) → stockage horaire (÷8).
      const cost = String(tjmToHourly(editForm.costRateOverride)).trim();
      const bill = String(tjmToHourly(editForm.billRateOverride)).trim();
      const payload = {
        roleOverride: role === '' ? null : role,
        // #108 — le CJM n'est envoyé que par l'ADMIN (refusé en 400 sinon).
        ...(showCostRates ? { costRateOverride: cost === '' ? null : cost } : {}),
        billRateOverride: bill === '' ? null : bill,
      };
      await apiAffaireTeam.update(affaireId, editing.id, payload);
      setEditing(null);
      await reload();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // synotis-support#9 — le retrait supprime définitivement le membre (il ne
  // rejoint plus les « membres archivés ») ; ses allocations et saisies
  // restent, ses taux négociés sur l'affaire sont perdus.
  const onRemove = async (m) => {
    if (
      !window.confirm(
        `Retirer ${m.fullName} de l'équipe ?\n\nLe retrait est définitif : ses taux négociés sur cette affaire seront perdus (ses allocations et saisies sont conservées).`,
      )
    ) {
      return;
    }
    try {
      await apiAffaireTeam.remove(affaireId, m.id);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const onRestore = async (m) => {
    try {
      await apiAffaireTeam.restore(affaireId, m.id);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const displayedArchived = useMemo(() => (showArchived ? archivedMembers : []), [showArchived, archivedMembers]);

  // Seules les lignes affichées sont sélectionnables : masquer les archivés
  // les retire de la sélection.
  const selectable = useMemo(() => [...members, ...displayedArchived], [members, displayedArchived]);
  const selectedMembers = selectable.filter((m) => selectedIds.has(m.id));
  const allSelected = selectable.length > 0 && selectedMembers.length === selectable.length;
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(selectable.map((m) => m.id)));

  // Retrait groupé : route unitaire par membre (mêmes droits et gardes
  // serveur), une seule confirmation, on poursuit en cas d'échec.
  const onBulkRemove = async () => {
    const targets = selectedMembers;
    const n = targets.length;
    if (
      !window.confirm(
        `Retirer ${n} membre${n > 1 ? 's' : ''} de l'équipe ?\n\nLe retrait est définitif : leurs taux négociés sur cette affaire seront perdus (allocations et saisies conservées).`,
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setError(null);
    const failures = [];
    for (const m of targets) {
      try {
        await apiAffaireTeam.remove(affaireId, m.id);
      } catch (e) {
        failures.push(`${m.fullName} : ${e.message}`);
      }
    }
    setBulkBusy(false);
    await reload();
    if (failures.length) setError(`${failures.length} échec(s) — ${failures.join(' · ')}`);
  };

  const bulkActions = [
    {
      key: 'remove',
      label: "Retirer de l'équipe",
      icon: Trash2,
      color: 'var(--danger)',
      count: selectedMembers.length,
      onClick: onBulkRemove,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Bandeau marge */}
      {margin && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
            padding: 12,
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <Metric label="Heures logguées" value={formatHours(margin.totalHours)} />
          {/* F-108-01 — coût et marge : ADMIN uniquement (non servis sinon). */}
          {showCostRates ? <Metric label="Coût total" value={formatMoney(margin.totalCost)} /> : null}
          <Metric label="Revenus" value={formatMoney(margin.totalRevenue)} />
          {showCostRates ? (
            <>
              <Metric
                label="Marge"
                value={formatMoney(margin.totalMargin)}
                highlight={Number(margin.totalMargin) >= 0 ? 'var(--success)' : 'var(--danger)'}
              />
              <Metric
                label="Marge / jour"
                value={margin.averageMarginPerHour ? formatRate(margin.averageMarginPerHour) : '—'}
              />
            </>
          ) : null}
        </div>
      )}

      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

      {/* Picker d'ajout */}
      {canEdit && (
        <div>
          {showPicker ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <PeoplePicker
                fetchOptions={fetchPickerOptions}
                onSelect={async (opt) => {
                  await onAdd(opt);
                  setShowPicker(false);
                }}
                autoFocus
                placeholder="Rechercher une personne à ajouter…"
                emptyMessage="Aucune personne disponible"
              />
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                style={{ ...secondaryBtn, alignSelf: 'flex-start' }}
              >
                Annuler
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowPicker(true)} style={primaryBtn}>
              <Plus size={14} /> Ajouter un membre
            </button>
          )}
        </div>
      )}

      {/* « Tout sélectionner » et la barre d'actions partagent une ligne de
          hauteur fixe : l'apparition de la barre ne décale plus la liste
          (un 2e clic rapide manquait sa case — recette synotis-support#9). */}
      {canEdit && selectable.length > 0 && !loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 48 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--text-muted)',
              paddingLeft: 12,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Tout sélectionner"
            />
            Tout sélectionner
          </label>
          <div style={{ flex: 1, minWidth: 0, marginBottom: -12 }}>
            <BulkActionsBar
              count={selectedMembers.length}
              actions={bulkActions}
              busy={bulkBusy}
              onClear={() => setSelectedIds(new Set())}
            />
          </div>
        </div>
      ) : null}

      {/* Liste des membres */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
      ) : members.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          Aucun membre dans l'équipe.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              canEdit={canEdit}
              showRates={showRates}
              showCostRates={showCostRates}
              selected={selectedIds.has(m.id)}
              onToggle={() => toggleOne(m.id)}
              onEdit={() => openEdit(m)}
              onRemove={() => onRemove(m)}
            />
          ))}
        </div>
      )}

      {/* Membres archivés (collapsible) */}
      {archivedMembers.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowArchived((s) => !s)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 12,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {showArchived ? 'Masquer' : 'Afficher'} les {archivedMembers.length} membre(s) archivé(s)
          </button>
          {showArchived && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {displayedArchived.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  canEdit={canEdit}
                  showRates={showRates}
                  showCostRates={showCostRates}
                  isArchived
                  selected={selectedIds.has(m.id)}
                  onToggle={() => toggleOne(m.id)}
                  onRestore={() => onRestore(m)}
                  onRemove={() => onRemove(m)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modale d'édition des overrides */}
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Overrides pour ${editing.fullName}` : ''}
        footer={
          <>
            <button type="button" onClick={() => setEditing(null)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="team-member-edit-form"
              disabled={submitting}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        {editing && (
          <form id="team-member-edit-form" onSubmit={onSaveEdit} style={{ display: 'grid', gap: 14 }}>
            <FieldOverride
              label="Rôle (override)"
              defaultLabel={editing.personRoleDefault}
              type="text"
              value={editForm.roleOverride}
              onChange={(v) => setEditForm({ ...editForm, roleOverride: v })}
              placeholder="Ex: Lead Architect"
              maxLength={128}
            />
            {showCostRates ? (
            <FieldOverride
              label="CJM (override, CHF/jour)"
              defaultLabel={editing.personCostRateDefault ? formatRate(editing.personCostRateDefault) : null}
              type="number"
              step="0.01"
              min="0"
              value={editForm.costRateOverride}
              onChange={(v) => setEditForm({ ...editForm, costRateOverride: v })}
            />
            ) : null}
            <FieldOverride
              label="TJM (override, CHF/jour)"
              defaultLabel={editing.personBillRateDefault ? formatRate(editing.personBillRateDefault) : null}
              type="number"
              step="0.01"
              min="0"
              value={editForm.billRateOverride}
              onChange={(v) => setEditForm({ ...editForm, billRateOverride: v })}
            />
            {showCostRates ? (
              <div
                style={{
                  marginTop: 4,
                  padding: 10,
                  background: 'var(--glass)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                Marge effective actuelle : <strong style={{ color: 'var(--text)' }}>{formatRate(editing.marginPerHour)}</strong>
              </div>
            ) : null}
            {editError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{editError}</div>}
          </form>
        )}
      </Modal>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </span>
      <span style={{ fontSize: 15, fontWeight: 600, color: highlight ?? 'var(--text)' }}>{value}</span>
    </div>
  );
}

function MemberRow({
  member,
  canEdit,
  showRates = true,
  showCostRates = true,
  isArchived = false,
  selected = false,
  onToggle,
  onEdit,
  onRemove,
  onRestore,
}) {
  const opacity = isArchived ? 0.5 : 1;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
        opacity,
      }}
    >
      {canEdit ? (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Sélectionner ${member.fullName}`}
          title={`Sélectionner ${member.fullName}`}
        />
      ) : null}
      <Avatar person={member} size={32} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{member.fullName}</span>
          {member.effectiveRole && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{member.effectiveRole}</span>
          )}
          {isArchived && (
            <span style={{ fontSize: 11, color: 'var(--warning)' }}>(archivé)</span>
          )}
        </div>
        {showRates ? (
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {showCostRates ? (
              <span>
                Coût&nbsp;: <strong style={{ color: 'var(--text)' }}>{formatRate(member.effectiveCostRate)}</strong>
                {member.costRateOverride !== null && <em style={{ marginLeft: 2 }}>*</em>}
              </span>
            ) : null}
            <span>
              Facturé&nbsp;: <strong style={{ color: 'var(--text)' }}>{formatRate(member.effectiveBillRate)}</strong>
              {member.billRateOverride !== null && <em style={{ marginLeft: 2 }}>*</em>}
            </span>
            {showCostRates ? (
              <span>
                Marge&nbsp;:{' '}
                <strong
                  style={{
                    color:
                      member.marginPerHour !== null
                        ? Number(member.marginPerHour) >= 0
                          ? 'var(--success)'
                          : 'var(--danger)'
                        : 'var(--text)',
                  }}
                >
                  {formatRate(member.marginPerHour)}
                </strong>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {canEdit && !isArchived && (
        <>
          <button type="button" onClick={onEdit} title="Éditer les overrides" aria-label="Éditer" style={iconBtn}>
            <Pencil size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            title="Retirer de l'équipe (définitif)"
            aria-label="Retirer"
            style={{ ...iconBtn, color: 'var(--danger)' }}
          >
            <Trash2 size={16} />
          </button>
        </>
      )}
      {canEdit && isArchived && (
        <>
          <button
            type="button"
            onClick={onRestore}
            title="Réintégrer"
            aria-label="Réintégrer"
            style={{ ...iconBtn, color: 'var(--success)' }}
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            title="Supprimer définitivement de l'équipe"
            aria-label="Supprimer"
            style={{ ...iconBtn, color: 'var(--danger)' }}
          >
            <Trash2 size={16} />
          </button>
        </>
      )}
    </div>
  );
}

function FieldOverride({ label, defaultLabel, value, onChange, ...rest }) {
  return (
    <div>
      <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-muted)' }}>
        {label}
      </label>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} {...rest} />
      {defaultLabel && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          Défaut Person&nbsp;: <strong style={{ color: 'var(--text)' }}>{defaultLabel}</strong>
        </div>
      )}
    </div>
  );
}
